import { socket } from '#AdminPanelFrontend/services/socket';
import Cookies from 'js-cookie';
import moment from 'moment/moment';
import React, { createContext, ReactNode, useEffect, useReducer } from 'react';

interface UserState {
	username: string | null;
	lssk: string | null;
	validUntil: number | null;
	isAuthorized: boolean;
}

const initialState: UserState = {
	username: null,
	lssk: null,
	validUntil: null,
	isAuthorized: false,
};

type Action = { type: 'LOGIN'; payload: Partial<UserState> } | { type: 'LOGOUT' };

const UserContext = createContext<{
	state: UserState;
	dispatch: React.Dispatch<Action>;
}>({
	state: initialState,
	dispatch: () => {},
});

const userReducer = (state: UserState, action: Action): UserState => {
	switch (action.type) {
		case 'LOGIN':
			const newState = { ...state, ...action.payload, isAuthorized: true };
			if (newState.lssk !== null && newState.validUntil !== null) {
				Cookies.set(
					'userStateAdmin',
					JSON.stringify({
						username: newState.username,
						lssk: newState.lssk,
						validUntil: newState.validUntil,
					}),
					{ expires: 7 }
				);
			}
			return newState;
		case 'LOGOUT':
			Cookies.remove('userStateAdmin');
			return initialState;
		default:
			return state;
	}
};

const UserProvider = ({ children }: { children: ReactNode }) => {
	const [state, dispatch] = useReducer(userReducer, initialState);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const ssoToken = params.get('sso_token');
		if (ssoToken) {
			window.history.replaceState({}, '', window.location.pathname);
			socket.timeout(10000).emit('User:SsoLogin', { ssoToken }, () => {});
		}

		const cookieState = Cookies.get('userStateAdmin');
		if (cookieState !== undefined) {
			const { username, lssk, validUntil } = JSON.parse(cookieState);
			if (moment(validUntil).unix() >= moment().unix()) {
				socket.timeout(5000).emit('User:Login', { username, lssk }, () => {});
			}
		}

		const onLogin = (data: { success?: boolean; data?: { username: string } }) => {
			if (data.success) {
				dispatch({ type: 'LOGIN', payload: { username: data.data!.username } });
			} else {
				dispatch({ type: 'LOGOUT' });
			}
		};

		const onSsoLogin = (data: { success?: boolean; data?: { username: string } }) => {
			if (data.success) {
				dispatch({ type: 'LOGIN', payload: { username: data.data!.username } });
			} else {
				dispatch({ type: 'LOGOUT' });
			}
		};

		const onRemember = (data: {
			success?: boolean;
			data?: { username: string; key: string; validUntil: number };
		}) => {
			if (data.success) {
				dispatch({
					type: 'LOGIN',
					payload: {
						username: data.data!.username,
						lssk: data.data!.key,
						validUntil: data.data!.validUntil,
					},
				});
			} else {
				dispatch({ type: 'LOGOUT' });
			}
		};

		socket.on('User:Login', onLogin);
		socket.on('User:SsoLogin', onSsoLogin);
		socket.on('User:Login:Remember', onRemember);

		return () => {
			socket.off('User:Login', onLogin);
			socket.off('User:SsoLogin', onSsoLogin);
			socket.off('User:Login:Remember', onRemember);
		};
	}, []);

	return <UserContext.Provider value={{ state, dispatch }}>{children}</UserContext.Provider>;
};

export { initialState, UserContext, UserProvider, userReducer };
